import { lazy, Suspense, useState, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import ErrorBoundary from '@/components/ErrorBoundary'
import { useAuth } from '@/contexts/AuthContext'
import { createClient } from '@/lib/supabase/client'

// 페이지 컴포넌트들을 lazy loading으로 변경 (코드 스플리팅)
const DashboardMain = lazy(() => import('@/components/dashboard/DashboardMain'))
const PurchaseNewMain = lazy(() => import('@/components/purchase/PurchaseNewMain'))
const RequestListMain = lazy(() => import('@/components/purchase/RequestListMain'))
const PurchaseDetailMain = lazy(() => import('@/components/purchase/PurchaseDetailMain'))
const VendorMain = lazy(() => import('@/components/vendor/VendorMain'))
const EmployeeMain = lazy(() => import('@/components/employee/EmployeeMain'))
const SupportMain = lazy(() => import('@/components/support/SupportMain'))
const ReceiptsMain = lazy(() => import('@/components/receipts/ReceiptsMain'))
const BomCoordinateMain = lazy(() => import('@/components/bom-coordinate/BomCoordinateIntegrated'))
const TransactionStatementMain = lazy(() => import('@/components/transaction-statement/TransactionStatementMain'))
const ApplicationListMain = lazy(() => import('@/components/application/ApplicationListMain'))
const AiServiceApplicationForm = lazy(() => import('@/components/application/AiServiceApplicationForm'))
const ShippingMain = lazy(() => import('@/components/shipping/ShippingMain'))
const AcceptanceMain = lazy(() => import('@/components/receipts/AcceptanceMain'))
const AttendancePage = lazy(() => import('@/components/employee/AttendancePage'))
const OfficialDocumentMain = lazy(() => import('@/components/official-document/OfficialDocumentMain'))
const AuthConfirm = lazy(() => import('@/components/auth/AuthConfirm'))
const ResetPassword = lazy(() => import('@/components/auth/ResetPassword'))
const SystemActivityLogsPage = lazy(() => import('@/components/logs/SystemActivityLogsPage'))
const ClientOrderListMain = lazy(() => import('@/components/production/ClientOrderListMain'))
const ProductionListMain = lazy(() => import('@/components/production/ProductionListMain'))

/**
 * 애플리케이션 라우팅 컴포넌트
 * - 인증된 사용자를 위한 모든 라우트 정의
 * - 지연 로딩 및 에러 바운더리 적용
 */
export default function AppRoutes() {
  const location = useLocation()
  
  return (
    <ErrorBoundary>
      <Suspense fallback={
        <div className="flex items-center justify-center py-12" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-hansl-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <span className="ml-3 text-gray-600 block mt-4">로딩 중...</span>
            <p className="text-xs text-gray-400 mt-2">경로: {location.pathname}</p>
          </div>
        </div>
      }>
        <Routes>
          {/* 기본 리다이렉트 */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* 테스트 라우트 */}
          <Route path="/test" element={<div style={{ padding: '20px', backgroundColor: 'yellow' }}>테스트 라우트 작동 중!</div>} />
          
          {/* 메인 페이지들 */}
          <Route path="/dashboard" element={<DashboardMain />} />
          <Route path="/shipping" element={<ShippingMain />} />
          <Route path="/shipping/acceptance" element={<AcceptanceMain />} />
          <Route path="/purchase" element={<RequestListMain showEmailButton={false} />} />
          <Route path="/purchase/new" element={<PurchaseNewMain />} />
          <Route path="/purchase/list" element={<RequestListMain showEmailButton={false} />} />
          <Route path="/purchase/detail/:id" element={<PurchaseDetailMain />} />
          <Route path="/purchase/requests/:id" element={<PurchaseDetailMain />} />
          <Route path="/vendor" element={<VendorMain />} />
          <Route path="/employee" element={<EmployeeMain />} />
          <Route path="/attendance" element={<AttendancePage />} />
          <Route path="/logs" element={<SystemActivityLogsPage />} />
          <Route path="/receipts" element={<ReceiptsMain />} />
          <Route path="/support" element={<SupportMain />} />
          <Route path="/application" element={<ApplicationListMain />} />
          <Route path="/application/ai-service" element={<AiServiceApplicationForm />} />
          <Route path="/bom-coordinate" element={<Navigate to="/bom-coordinate/new" replace />} />
          <Route path="/bom-coordinate/new" element={<BomCoordinateMain />} />
          <Route path="/bom-coordinate/list" element={<BomCoordinateMain />} />
          <Route path="/transaction-statement" element={<TransactionStatementMain />} />
          <Route path="/official-document" element={<OfficialDocumentMain />} />
          <Route path="/client-orders" element={<ClientOrderListMain />} />
          <Route path="/production" element={<ProductionListMain />} />
          
          {/* 인증 관련 라우트 */}
          <Route path="/auth/confirm" element={<AuthConfirm />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          
          {/* 알 수 없는 경로는 대시보드로 리다이렉트 */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}

function RootRedirect() {
  const { currentUserEmail, loading: authLoading } = useAuth()
  const [targetPath, setTargetPath] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) return

    if (!currentUserEmail) {
      setTargetPath('/dashboard')
      setLoading(false)
      return
    }

    const loadPreference = async () => {
      try {
        const supabase = createClient()
        const { data, error } = await supabase
          .from('user_ui_settings')
          .select('setting_value')
          .eq('user_email', currentUserEmail)
          .eq('setting_type', 'general')
          .eq('setting_key', 'default_landing_system')
          .maybeSingle()

        if (!error && data?.setting_value) {
          const val = data.setting_value as { system?: string }
          if (val.system === 'client-orders') {
            setTargetPath('/client-orders')
          } else if (val.system === 'production') {
            setTargetPath('/production')
          } else {
            setTargetPath('/dashboard')
          }
        } else {
          setTargetPath('/dashboard')
        }
      } catch (err) {
        setTargetPath('/dashboard')
      } finally {
        setLoading(false)
      }
    }

    loadPreference()
  }, [currentUserEmail, authLoading])

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-hansl-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return <Navigate to={targetPath || '/dashboard'} replace />
}